import apiClient from '../api/client';
import {
  InventoryCompound,
  RegistryCompound,
  InventoryBatch,
  InventorySample,
  InventoryLocation,
  InventoryEvent,
  InventorySettings,
  InventoryDashboard,
  CompoundIntelligence,
} from '../types/inventory';

// =====================================================================
// API Client
// =====================================================================

const BASE = '/inventory';

export const inventoryAPI = {
  // ── Settings ──
  getSettings: () =>
    apiClient.get<InventorySettings>(`${BASE}/settings`),

  updateSettings: (data: Partial<InventorySettings>) =>
    apiClient.put(`${BASE}/settings`, data),

  // ── Registry Search (for compound selector in Add to Stock) ──
  registrySearch: (q: string) =>
    apiClient.get<RegistryCompound[]>(`${BASE}/registry-search`, { params: { q } }),

  // ── Add to Stock (compound + batch + sample in one transaction) ──
  addToStock: (data: {
    registry_parent_id?: number;
    name?: string;
    smiles?: string;
    category?: string;
    purity_percent?: number;
    salt_form?: string;
    polymorph?: string;
    supplier?: string;
    notebook_ref?: string;
    synthesis_date?: string;
    amount_made?: number;
    amount_unit?: string;
    container_type?: string;
    current_amount?: number;
    location_id?: number;
    barcode?: string;
    expiry_date?: string;
    notes?: string;
  }) =>
    apiClient.post(`${BASE}/add-to-stock`, data),

  // ── Compounds ──
  queryCompounds: (params: {
    startRow: number;
    endRow: number;
    sortModel?: Array<{ colId: string; sort: string }>;
    filterModel?: Record<string, any>;
  }) =>
    apiClient.post<{ rows: InventoryCompound[]; lastRow: number }>(
      `${BASE}/compounds/query`, params
    ),

  getCompound: (compoundId: number) =>
    apiClient.get<InventoryCompound>(`${BASE}/compounds/${compoundId}`),

  getIntelligence: (compoundId: number) =>
    apiClient.get<CompoundIntelligence>(`${BASE}/compounds/${compoundId}/intelligence`),

  // ── Batches ──
  createBatch: (compoundId: number, data: {
    purity_percent?: number;
    polymorph?: string;
    salt_form?: string;
    amount_made?: number;
    amount_unit?: string;
    supplier?: string;
    notebook_ref?: string;
    synthesis_date?: string;
    notes?: string;
  }) =>
    apiClient.post(`${BASE}/compounds/${compoundId}/batches`, data),

  getBatch: (batchId: number) =>
    apiClient.get<InventoryBatch>(`${BASE}/batches/${batchId}`),

  // ── Samples ──
  createSample: (batchId: number, data: {
    location_id?: number;
    barcode?: string;
    current_amount?: number;
    amount_unit?: string;
    expiry_date?: string;
    notes?: string;
  }) =>
    apiClient.post(`${BASE}/batches/${batchId}/samples`, data),

  updateSample: (sampleId: number, data: {
    current_amount: number;
    amount_unit: string;
    container_type: string;
    location_id?: number;
    status: string;
    expiry_date?: string;
    notes?: string;
  }) =>
    apiClient.put(`${BASE}/samples/${sampleId}`, data),

  dispenseSample: (sampleId: number, data: { amount: number; notes?: string }) =>
    apiClient.post(`${BASE}/samples/${sampleId}/dispense`, data),

  moveSample: (sampleId: number, data: { location_id: number; notes?: string }) =>
    apiClient.post(`${BASE}/samples/${sampleId}/move`, data),

  reserveSample: (sampleId: number, data?: { notes?: string; days?: number }) =>
    apiClient.post(`${BASE}/samples/${sampleId}/reserve`, data || {}),

  releaseSample: (sampleId: number) =>
    apiClient.post(`${BASE}/samples/${sampleId}/release`),

  lookupBarcode: (code: string) =>
    apiClient.get<InventorySample>(`${BASE}/samples/barcode/${encodeURIComponent(code)}`),

  // ── Locations ──
  getLocationTree: () =>
    apiClient.get<InventoryLocation[]>(`${BASE}/locations`),

  createLocation: (data: {
    parent_id?: number;
    name: string;
    location_type?: string;
    temperature_zone?: string;
    capacity?: number;
    notes?: string;
  }) =>
    apiClient.post(`${BASE}/locations`, data),

  updateLocation: (locationId: number, data: {
    name?: string;
    location_type?: string;
    temperature_zone?: string;
    capacity?: number;
    notes?: string;
  }) =>
    apiClient.put(`${BASE}/locations/${locationId}`, data),

  deleteLocation: (locationId: number) =>
    apiClient.delete(`${BASE}/locations/${locationId}`),

  // ── Dashboard & Events ──
  getDashboard: () =>
    apiClient.get<InventoryDashboard>(`${BASE}/dashboard`),

  getEvents: (params?: { limit?: number; offset?: number; entity_type?: string }) =>
    apiClient.get<InventoryEvent[]>(`${BASE}/events`, { params }),
};
